import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { format, parseISO, differenceInDays } from 'date-fns';
import { 
  User, Building, Plus, Trash2, Calendar, Clock, Star, 
  Loader2, ChevronRight, CreditCard, AlertCircle, Check, X,
  CheckCircle, XCircle, Ban, CalendarClock, Trophy, Edit, 
  Settings, Award, Heart, Flame
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { PresignedImage } from '../components/PresignedMedia';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  confirmed: 'bg-green-500/20 text-green-400 border-green-500/30',
  completed: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  cancelled: 'bg-red-500/20 text-red-400 border-red-500/30',
  declined: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  active: 'bg-green-500/20 text-green-400 border-green-500/30',
  inactive: 'bg-neutral-500/20 text-neutral-400 border-neutral-500/30'
};

export default function UserDashboard() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [businesses, setBusinesses] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteModal, setDeleteModal] = useState({ open: false, business: null });
  const [deleting, setDeleting] = useState(false);
  const [activeBookingTab, setActiveBookingTab] = useState('upcoming');
  const [reviewModal, setReviewModal] = useState({ open: false, booking: null });
  const [reviewForm, setReviewForm] = useState({ rating: 5, comment: '' });
  const [submitting, setSubmitting] = useState(false);
  
  // User profile state
  const [profile, setProfile] = useState(null);
  const [editingUsername, setEditingUsername] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [usernameLoading, setUsernameLoading] = useState(false);

  useEffect(() => {
    fetchData();
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/users/me/profile`);
      setProfile(response.data);
      setNewUsername(response.data.anonymous_username || '');
    } catch (error) {
      console.error('Failed to fetch profile:', error);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [businessesRes, bookingsRes] = await Promise.all([
        axios.get(`${API}/users/my/businesses`),
        axios.get(`${API}/bookings`).catch(() => ({ data: { bookings: [] } }))
      ]);
      setBusinesses(businessesRes.data.businesses || []);
      setBookings(bookingsRes.data.bookings || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  // Check if username can be changed (once per month)
  const canChangeUsername = () => {
    if (!profile?.last_username_change) return true;
    const lastChange = parseISO(profile.last_username_change);
    const daysSince = differenceInDays(new Date(), lastChange);
    return daysSince >= 30;
  };

  const handleUsernameChange = async () => {
    if (!newUsername.trim() || newUsername.length < 3 || newUsername.length > 20) {
      toast.error('Username must be 3-20 characters');
      return;
    }
    
    if (!/^[a-zA-Z0-9]+$/.test(newUsername)) {
      toast.error('Username must be alphanumeric only');
      return;
    }

    setUsernameLoading(true);
    try {
      await axios.put(`${API}/users/me/username`, {
        anonymous_username: newUsername
      });
      toast.success('Username updated!');
      setEditingUsername(false);
      await refreshUser();
      fetchProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update username');
    } finally {
      setUsernameLoading(false);
    }
  };

  const handleDeleteBusiness = async () => {
    if (!deleteModal.business) return;
    
    setDeleting(true);
    try {
      const response = await axios.delete(`${API}/businesses/${deleteModal.business.id}`);
      toast.success(response.data.message);
      setDeleteModal({ open: false, business: null });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete business');
    } finally {
      setDeleting(false);
    }
  };

  const handleSubmitReview = async () => {
    if (!reviewForm.comment.trim()) {
      toast.error('Please write a review');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews`, {
        booking_id: reviewModal.booking.id,
        rating: reviewForm.rating,
        comment: reviewForm.comment
      });
      toast.success('Review submitted!');
      setReviewModal({ open: false, booking: null });
      setReviewForm({ rating: 5, comment: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  const renderStars = (rating, interactive = false, onChange = null) => {
    return (
      <div className="flex gap-1">
        {Array.from({ length: 5 }, (_, i) => (
          <button
            key={i}
            type="button"
            onClick={() => interactive && onChange && onChange(i + 1)}
            className={interactive ? 'cursor-pointer' : 'cursor-default'}
          >
            <Star 
              className={`w-6 h-6 transition-colors ${
                i < rating 
                  ? 'fill-yellow-400 text-yellow-400' 
                  : 'text-neutral-600 hover:text-yellow-400'
              }`} 
            />
          </button>
        ))}
      </div>
    );
  };

  // Booking stats
  const upcomingBookings = bookings.filter(b => ['pending', 'confirmed'].includes(b.status));
  const completedBookings = bookings.filter(b => b.status === 'completed');
  const cancelledBookings = bookings.filter(b => b.status === 'cancelled');
  const declinedBookings = bookings.filter(b => b.status === 'declined');

  // Filter bookings based on active tab
  const filteredBookings = bookings.filter(booking => {
    if (activeBookingTab === 'upcoming') {
      return ['pending', 'confirmed'].includes(booking.status);
    } else if (activeBookingTab === 'completed') {
      return booking.status === 'completed';
    } else if (activeBookingTab === 'cancelled') {
      return booking.status === 'cancelled';
    } else if (activeBookingTab === 'declined') {
      return booking.status === 'declined';
    }
    return true;
  });

  const activeSubscriptions = businesses.filter(b => 
    b.subscription_status === 'active'
  );

  const hasBusinesses = businesses.length > 0;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-red-500" />
      </div>
    );
  }

  // Render My Bookings Section Component
  const renderMyBookingsSection = () => (
    <div className="bg-[#121212] rounded-2xl border border-white/10 p-6 mb-8">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-white flex items-center gap-2">
          <Calendar className="w-5 h-5 text-red-500" />
          My Bookings
        </h2>
        <Link to="/businesses" className="text-sm text-red-400 hover:text-red-300">
          Browse Services
        </Link>
      </div>

      {/* Booking Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <div 
          onClick={() => setActiveBookingTab('upcoming')}
          className={`p-4 rounded-xl cursor-pointer transition-all ${
            activeBookingTab === 'upcoming' 
              ? 'bg-yellow-500/20 border border-yellow-500/30' 
              : 'bg-white/5 hover:bg-white/10'
          }`}
        >
          <div className="flex items-center gap-2 mb-1">
            <CalendarClock className="w-4 h-4 text-yellow-400" />
            <p className="text-xs text-neutral-400">Upcoming</p>
          </div>
          <p className="text-2xl font-bold text-yellow-400">{upcomingBookings.length}</p>
        </div>
        <div 
          onClick={() => setActiveBookingTab('completed')}
          className={`p-4 rounded-xl cursor-pointer transition-all ${
            activeBookingTab === 'completed' 
              ? 'bg-blue-500/20 border border-blue-500/30' 
              : 'bg-white/5 hover:bg-white/10'
          }`}
        >
          <div className="flex items-center gap-2 mb-1">
            <CheckCircle className="w-4 h-4 text-blue-400" />
            <p className="text-xs text-neutral-400">Completed</p>
          </div>
          <p className="text-2xl font-bold text-blue-400">{completedBookings.length}</p>
        </div>
        <div 
          onClick={() => setActiveBookingTab('cancelled')}
          className={`p-4 rounded-xl cursor-pointer transition-all ${
            activeBookingTab === 'cancelled' 
              ? 'bg-red-500/20 border border-red-500/30' 
              : 'bg-white/5 hover:bg-white/10'
          }`}
        >
          <div className="flex items-center gap-2 mb-1">
            <XCircle className="w-4 h-4 text-red-400" />
            <p className="text-xs text-neutral-400">Cancelled</p>
          </div>
          <p className="text-2xl font-bold text-red-400">{cancelledBookings.length}</p>
        </div>
        <div 
          onClick={() => setActiveBookingTab('declined')}
          className={`p-4 rounded-xl cursor-pointer transition-all ${
            activeBookingTab === 'declined' 
              ? 'bg-orange-500/20 border border-orange-500/30' 
              : 'bg-white/5 hover:bg-white/10'
          }`}
        >
          <div className="flex items-center gap-2 mb-1">
            <Ban className="w-4 h-4 text-orange-400" />
            <p className="text-xs text-neutral-400">Declined</p>
          </div>
          <p className="text-2xl font-bold text-orange-400">{declinedBookings.length}</p>
        </div>
      </div>

      {/* Bookings List */}
      {filteredBookings.length === 0 ? (
        <div className="text-center py-8">
          <Calendar className="w-10 h-10 text-neutral-600 mx-auto mb-3" />
          <p className="text-neutral-400">
            {activeBookingTab === 'upcoming' && 'No upcoming bookings'}
            {activeBookingTab === 'completed' && 'No completed bookings'}
            {activeBookingTab === 'cancelled' && 'No cancelled bookings'}
            {activeBookingTab === 'declined' && 'No declined bookings'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredBookings.map(booking => (
            <div key={booking.id} className="p-4 bg-white/5 rounded-xl">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <Link 
                    to={`/businesses/${booking.business_id}`}
                    className="font-medium text-white hover:text-red-400 transition-colors"
                  >
                    {booking.business_name}
                  </Link>
                  <p className="text-sm text-neutral-400">{booking.service_name}</p>
                </div>
                <Badge className={statusColors[booking.status]}>
                  {booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
                </Badge>
              </div>
              <div className="flex items-center gap-3 text-xs text-neutral-500">
                <span className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {format(parseISO(booking.date), 'MMM d, yyyy')}
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {booking.time_slot}
                </span>
              </div>
              
              {/* Show pending confirmation message */}
              {booking.status === 'pending' && (
                <div className="mt-3 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Clock className="w-3 h-3 text-yellow-400" />
                    <p className="text-xs text-yellow-400 font-medium">Awaiting confirmation from business</p>
                  </div>
                </div>
              )}
              
              {/* Show decline reason if declined */}
              {booking.status === 'declined' && booking.decline_reason && (
                <div className="mt-3 p-3 bg-orange-500/10 border border-orange-500/20 rounded-lg">
                  <p className="text-xs text-orange-400 font-medium mb-1">Reason for decline:</p>
                  <p className="text-sm text-neutral-300">{booking.decline_reason}</p>
                </div>
              )}
              
              {/* Review button for completed bookings */}
              {booking.status === 'completed' && !booking.review_submitted && (
                <Button 
                  onClick={() => setReviewModal({ open: true, booking })}
                  className="mt-3 bg-red-500/10 text-red-400 hover:bg-red-500/20 rounded-full"
                  size="sm"
                >
                  <Star className="w-4 h-4 mr-2" />
                  Leave a Review
                </Button>
              )}
              
              {booking.review_submitted && (
                <div className="mt-3 flex items-center gap-2 text-sm text-green-400">
                  <Check className="w-4 h-4" />
                  Review submitted
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // Render Business Stats Section (only for users with businesses)
  const renderBusinessStats = () => (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
      <div className="bg-[#121212] rounded-2xl border border-white/10 p-6">
        <p className="text-sm text-neutral-400 mb-1">My Businesses</p>
        <p className="text-3xl font-bold text-white">{businesses.length}</p>
      </div>
      <div className="bg-[#121212] rounded-2xl border border-white/10 p-6">
        <p className="text-sm text-neutral-400 mb-1">Active Subscriptions</p>
        <p className="text-3xl font-bold text-green-400">{activeSubscriptions.length}</p>
      </div>
      <div className="bg-[#121212] rounded-2xl border border-white/10 p-6">
        <p className="text-sm text-neutral-400 mb-1">Upcoming Bookings</p>
        <p className="text-3xl font-bold text-yellow-400">{upcomingBookings.length}</p>
      </div>
      <div className="bg-[#121212] rounded-2xl border border-white/10 p-6">
        <p className="text-sm text-neutral-400 mb-1">Monthly Cost</p>
        <p className="text-3xl font-bold text-white font-mono">${activeSubscriptions.length * 100}</p>
      </div>
    </div>
  );

  // Render My Businesses Section
  const renderMyBusinessesSection = () => (
    <div className="bg-[#121212] rounded-2xl border border-white/10 p-6 mb-8">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-white flex items-center gap-2">
          <Building className="w-5 h-5 text-red-500" />
          My Businesses
        </h2>
        <Link to="/business/create">
          <Button className="bg-red-500 hover:bg-red-600 rounded-full">
            <Plus className="w-4 h-4 mr-2" />
            Add Business
          </Button>
        </Link>
      </div>

      <div className="space-y-4">
        {businesses.map(business => (
          <div 
            key={business.id}
            className="bg-white/5 rounded-xl p-4 hover:bg-white/10 transition-colors"
          >
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="text-lg font-semibold text-white">{business.business_name}</h3>
                  <Badge className={statusColors[business.subscription_status || 'inactive']}>
                    {business.subscription_status === 'active' ? 'Active' : 
                     business.subscription_status === 'cancelled' ? 'Cancelled' : 
                     business.subscription_status === 'trial' ? 'Trial' : 'Inactive'}
                  </Badge>
                </div>
                <p className="text-sm text-neutral-400 mb-2">
                  {business.category?.replace(/_/g, ' ')} • /shop/{business.handle}
                </p>
                {business.subscription_status === 'active' && business.subscription_expires && (
                  <p className="text-xs text-neutral-500">
                    Subscription expires: {format(parseISO(business.subscription_expires), 'MMM d, yyyy')}
                  </p>
                )}
                {business.subscription_status === 'cancelled' && business.subscription_expires && (
                  <p className="text-xs text-yellow-500">
                    Access until: {format(parseISO(business.subscription_expires), 'MMM d, yyyy')}
                  </p>
                )}
              </div>
              
              <div className="flex items-center gap-2">
                <Link to={`/dashboard/${business.id}/overview`}>
                  <Button variant="outline" className="border-white/20 text-white hover:bg-white/10 rounded-full">
                    Manage
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
                <Button 
                  variant="ghost" 
                  size="icon"
                  onClick={() => setDeleteModal({ open: true, business })}
                  className="text-red-400 hover:bg-red-500/10 rounded-full"
                  data-testid={`delete-business-${business.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // Render Active Subscriptions Section
  const renderActiveSubscriptions = () => (
    activeSubscriptions.length > 0 && (
      <div className="bg-gradient-to-br from-green-900/30 to-green-800/10 rounded-2xl border border-green-500/30 p-6 mb-8">
        <div className="flex items-center gap-2 mb-4">
          <CreditCard className="w-5 h-5 text-green-400" />
          <h2 className="text-lg font-bold text-white">Active Subscriptions</h2>
        </div>
        <div className="space-y-3">
          {activeSubscriptions.map(business => (
            <div key={business.id} className="flex items-center justify-between p-3 bg-white/5 rounded-xl">
              <div>
                <p className="font-medium text-white">{business.business_name}</p>
                <p className="text-sm text-neutral-400">
                  Renews: {business.subscription_expires ? format(parseISO(business.subscription_expires), 'MMM d, yyyy') : 'N/A'}
                </p>
              </div>
              <p className="text-green-400 font-mono font-bold">$100/mo</p>
            </div>
          ))}
          <div className="flex items-center justify-between pt-3 border-t border-white/10">
            <p className="text-neutral-400">Total Monthly</p>
            <p className="text-xl font-bold text-white font-mono">${activeSubscriptions.length * 100}/mo</p>
          </div>
        </div>
      </div>
    )
  );

  // Render Start Business CTA
  const renderStartBusinessCTA = () => (
    <div className="bg-gradient-to-br from-red-900/30 to-red-800/10 rounded-2xl border border-red-500/30 p-8 text-center">
      <Building className="w-12 h-12 text-red-400 mx-auto mb-4" />
      <h3 className="text-xl font-bold text-white mb-2">Start Your Business</h3>
      <p className="text-neutral-400 mb-6 max-w-md mx-auto">
        Ready to take your services online? Create a business profile and start accepting bookings from customers.
      </p>
      <Link to="/business/create">
        <Button className="bg-red-500 hover:bg-red-600 rounded-full px-8">
          <Plus className="w-4 h-4 mr-2" />
          Create Business
        </Button>
      </Link>
    </div>
  );

  // Render Sidequest Stats Section
  const renderSidequestStats = () => (
    <div className="bg-gradient-to-br from-red-900/20 to-[#121212] rounded-2xl border border-red-500/20 p-6 mb-8">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
        {/* Username Section */}
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center">
            <Flame className="w-7 h-7 text-white" />
          </div>
          <div>
            {editingUsername ? (
              <div className="flex items-center gap-2">
                <Input
                  value={newUsername}
                  onChange={(e) => setNewUsername(e.target.value)}
                  placeholder="Username"
                  className="bg-white/10 border-white/20 text-white w-40 h-9"
                  maxLength={20}
                />
                <Button 
                  size="sm"
                  onClick={handleUsernameChange}
                  disabled={usernameLoading}
                  className="bg-green-500 hover:bg-green-600 h-9"
                >
                  {usernameLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                </Button>
                <Button 
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setEditingUsername(false);
                    setNewUsername(profile?.anonymous_username || '');
                  }}
                  className="text-neutral-400 h-9"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-bold text-white">@{profile?.anonymous_username || 'loading...'}</h2>
                {canChangeUsername() && (
                  <button
                    onClick={() => setEditingUsername(true)}
                    className="text-neutral-400 hover:text-white"
                    title="Edit username"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                )}
              </div>
            )}
            <p className="text-neutral-400 text-sm">
              {canChangeUsername() ? 'Click to edit username' : `Username can be changed in ${30 - differenceInDays(new Date(), parseISO(profile?.last_username_change || new Date().toISOString()))} days`}
            </p>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white/5 rounded-xl p-4 text-center">
            <Trophy className="w-5 h-5 text-yellow-400 mx-auto mb-2" />
            <p className="text-2xl font-bold text-white">{profile?.sidequest_stats?.total_completed || 0}</p>
            <p className="text-xs text-neutral-400">Completed</p>
          </div>
          <div className="bg-white/5 rounded-xl p-4 text-center">
            <Clock className="w-5 h-5 text-green-400 mx-auto mb-2" />
            <p className="text-2xl font-bold text-green-400">{profile?.sidequest_stats?.on_time || 0}</p>
            <p className="text-xs text-neutral-400">On Time</p>
          </div>
          <div className="bg-white/5 rounded-xl p-4 text-center">
            <Heart className="w-5 h-5 text-red-400 mx-auto mb-2" />
            <p className="text-2xl font-bold text-red-400">{profile?.total_likes_received || 0}</p>
            <p className="text-xs text-neutral-400">Likes</p>
          </div>
          <div className="bg-white/5 rounded-xl p-4 text-center">
            <Award className="w-5 h-5 text-purple-400 mx-auto mb-2" />
            <p className="text-2xl font-bold text-purple-400">{profile?.badges?.length || 0}</p>
            <p className="text-xs text-neutral-400">Badges</p>
          </div>
        </div>
      </div>

      {/* Badges Row */}
      {profile?.badges && profile.badges.length > 0 && (
        <div className="mt-4 pt-4 border-t border-white/10">
          <p className="text-sm text-neutral-400 mb-3">Earned Badges</p>
          <div className="flex flex-wrap gap-3">
            {profile.badges.map(badge => (
              <div 
                key={badge.id} 
                className="flex items-center gap-2 bg-white/5 rounded-full px-3 py-1.5"
                title={badge.description}
              >
                {badge.icon_url ? (
                  <PresignedImage 
                    src={badge.icon_url}
                    alt={badge.name}
                    className="w-5 h-5 rounded-full object-contain"
                  />
                ) : (
                  <Award className="w-4 h-4 text-yellow-400" />
                )}
                <span className="text-sm text-white">{badge.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Settings Link */}
      <div className="mt-4 flex justify-end">
        <Link to="/settings" className="text-neutral-400 hover:text-white text-sm flex items-center gap-1.5">
          <Settings className="w-4 h-4" />
          Settings
        </Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0A0A0A] px-4 sm:px-6 py-8" data-testid="user-dashboard">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-red-500 to-red-700 flex items-center justify-center">
              <User className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white">{user?.name || 'User'}</h1>
              <p className="text-neutral-400">{user?.email}</p>
            </div>
          </div>
        </div>

        {/* Sidequest Stats - Always shown */}
        {renderSidequestStats()}

        {hasBusinesses ? (
          // Layout for users WITH businesses
          <>
            {renderBusinessStats()}
            {renderMyBusinessesSection()}
            {renderActiveSubscriptions()}
            {renderMyBookingsSection()}
          </>
        ) : (
          // Layout for users WITHOUT businesses
          <>
            {renderMyBookingsSection()}
            {renderStartBusinessCTA()}
          </>
        )}
      </div>

      {/* Delete Business Modal */}
      <Dialog open={deleteModal.open} onOpenChange={(open) => setDeleteModal({ open, business: deleteModal.business })}>
        <DialogContent className="bg-[#121212] border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-500" />
              Delete Business
            </DialogTitle>
            <DialogDescription className="text-neutral-400">
              Are you sure you want to delete <span className="text-white font-medium">{deleteModal.business?.business_name}</span>?
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {deleteModal.business?.subscription_status === 'active' ? (
              <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-xl">
                <p className="text-yellow-400 text-sm">
                  <strong>Note:</strong> This business has an active subscription. The subscription will be cancelled but you'll retain access until the end of the billing period ({deleteModal.business?.subscription_expires ? format(parseISO(deleteModal.business.subscription_expires), 'MMM d, yyyy') : 'expiry'}).
                </p>
              </div>
            ) : (
              <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-xl">
                <p className="text-red-400 text-sm">
                  <strong>Warning:</strong> This action cannot be undone. All business data, bookings, and reviews will be permanently deleted.
                </p>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => setDeleteModal({ open: false, business: null })}
              className="border-white/20 text-white hover:bg-white/10"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleDeleteBusiness}
              disabled={deleting}
              className="bg-red-500 hover:bg-red-600"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Delete Business
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Review Modal */}
      <Dialog open={reviewModal.open} onOpenChange={(open) => setReviewModal({ open, booking: reviewModal.booking })}>
        <DialogContent className="bg-[#121212] border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Leave a Review</DialogTitle>
            <DialogDescription className="text-neutral-400">
              Share your experience with {reviewModal.booking?.business_name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 mt-4">
            <div>
              <label className="block text-sm font-medium text-white mb-3">Rating</label>
              {renderStars(reviewForm.rating, true, (rating) => setReviewForm(f => ({ ...f, rating })))}
            </div>

            <div>
              <label className="block text-sm font-medium text-white mb-2">Your Review</label>
              <Textarea 
                value={reviewForm.comment}
                onChange={(e) => setReviewForm(f => ({ ...f, comment: e.target.value }))}
                placeholder="Tell others about your experience..."
                className="bg-neutral-900 border-white/10 text-white resize-none"
                rows={4}
              />
            </div>

            <Button 
              onClick={handleSubmitReview}
              disabled={submitting}
              className="w-full bg-red-500 hover:bg-red-600 rounded-full"
            >
              {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Submit Review'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
